import React, { Component } from "react"
import PropTypes from "prop-types"
import { connectAdvanced } from "react-redux"
import Mutation from "./Mutation"
import { STORE_KEY } from "./Store"
import invariant from "invariant"

export default function provideMutation(options = {}) {
  const rootProp = options.rootProp || "root"
  const defaultMutationKey = options.mutationKey

  return function(TargetComponent) {
    class WrappedComponent extends Component {
      static propTypes = {
        inheritMutation: PropTypes.bool,
        locationKey: PropTypes.string,
        mutationKey: PropTypes.string,
        mutationRef: PropTypes.func,
        store: PropTypes.object,
      }

      static displayName = `ProvideMutation(${TargetComponent.name})`

      static childContextTypes = { mutation: PropTypes.instanceOf(Mutation) }

      static contextTypes = { mutation: PropTypes.instanceOf(Mutation) }

      getChildContext() {
        const mutation = this._mutation || this._resolveMutation()

        return { mutation }
      }

      componentDidMount() {
        this._manageMutation(this.props)
      }

      // eslint-disable-next-line camelcase
      UNSAFE_componentWillReceiveProps(props) {
        this._manageMutation(props)
      }

      _updateMutationRef() {
        const { mutationRef } = this.props

        if (mutationRef) {
          mutationRef(this._mutation)
        }
      }

      _resolveMutation(props, existingMutation) {
        const {
          inheritMutation,
          mutationKey: mutationKeyProp,
          locationKey,
          store: { dispatch, state: { [STORE_KEY]: state } },
          [rootProp]: root,
        } = props

        const { mutation: parentMutation } = this.context

        if (inheritMutation && parentMutation) {
          return parentMutation
        } else {
          let mutationKey = mutationKeyProp || defaultMutationKey
          if (!mutationKey && locationKey) {
            mutationKey = `${locationKey}.mutation`
          }
          invariant(mutationKey, "Must provide a mutationKey or a parent mutation")
          invariant(
            mutationKey && !/undefined/.test(mutationKey),
            `mutationKey ${mutationKey} has undefined components`
          )

          if (locationKey && mutationKey.indexOf(".") < 0) {
            mutationKey = `${locationKey}.${mutationKey}`
          }

          let mutation = existingMutation

          if (
            !existingMutation ||
            existingMutation.mutationKey != mutationKey ||
            root !== existingMutation.root
          ) {
            mutation = new Mutation(dispatch, () => state || {}, mutationKey, root)
          }

          if (state != mutation.getState()) {
            mutation.attach(dispatch, () => state)
          }

          return mutation
        }
      }

      _manageMutation(props) {
        const existingMutation = this._mutation
        this._mutation = this._resolveMutation(props, existingMutation)

        if (this._mutation != existingMutation) {
          this._updateMutationRef()
        }
      }

      render() {
        const {
          inheritMutation,
          mutationKey: mutationKeyProp,
          locationKey,
          store,
          [rootProp]: root,

          ...otherProps
        } = this.props

        const proxiedProps = { mutation: this._mutation, ...otherProps }

        return <TargetComponent {...proxiedProps} />
      }
    }

    const mapper = (dispatch, _options) => (state, props) => ({
      ...props,
      store: { dispatch, state, props },
    })

    return connectAdvanced(mapper)(WrappedComponent)
  }
}



// WEBPACK FOOTER //
// ../ui-kit/src/lib/store/Mutation/provideMutation.js